import { EnumBaselineProp } from '../../../../models/schema';
import { HostedContentFilterPolicy_SchemaData, HostedContentFilterPolicySchema } from './model';

export function updateSchema(formValue: any, schema: HostedContentFilterPolicySchema): HostedContentFilterPolicySchema {
    const actionKeys = ['SpamAction', 'HighConfidenceSpamAction', 'PhishSpamAction', 'BulkSpamAction'] as const;

    const buildArrayProp = (items: string[]): EnumBaselineProp => {
        if (items.length > 0) {
            return {
                type: 'array',
                items: { type: 'string', enum: items },
                minItems: items.length,
                maxItems: items.length,
                uniqueItems: true,
            };
        } else {
            return {
                type: 'array',
                minItems: 0,
                maxItems: 0,
                uniqueItems: true,
            };
        }
    };

    const properties: HostedContentFilterPolicy_SchemaData = {
        Name: schema.contains.properties.Name,

        SpamAction: { type: 'string', const: formValue.SpamAction },
        HighConfidenceSpamAction: { type: 'string', const: formValue.HighConfidenceSpamAction },
        PhishSpamAction: { type: 'string', const: formValue.PhishSpamAction },
        HighConfidencePhishAction: { type: 'string', const: formValue.HighConfidencePhishAction },
        BulkSpamAction: { type: 'string', const: formValue.BulkSpamAction },

        IntraOrgFilterState: { type: 'string', const: formValue.IntraOrgFilterState },
        InlineSafetyTipsEnabled: { type: 'boolean', const: formValue.InlineSafetyTipsEnabled },
        QuarantineRetentionPeriod: { type: 'number', const: formValue.QuarantineRetentionPeriod },
        BulkThreshold: { type: 'number', const: formValue.BulkThreshold },

        IncreaseScoreWithBizOrInfoUrls: { type: 'string', const: formValue.IncreaseScoreWithBizOrInfoUrls },
        IncreaseScoreWithImageLinks: { type: 'string', const: formValue.IncreaseScoreWithImageLinks },
        IncreaseScoreWithNumericIps: { type: 'string', const: formValue.IncreaseScoreWithNumericIps },
        IncreaseScoreWithRedirectToOtherPort: { type: 'string', const: formValue.IncreaseScoreWithRedirectToOtherPort },

        MarkAsSpamEmptyMessages: { type: 'string', const: formValue.MarkAsSpamEmptyMessages },
        MarkAsSpamJavaScriptInHtml: { type: 'string', const: formValue.MarkAsSpamJavaScriptInHtml },
        MarkAsSpamObjectTagsInHtml: { type: 'string', const: formValue.MarkAsSpamObjectTagsInHtml },
        MarkAsSpamFramesInHtml: { type: 'string', const: formValue.MarkAsSpamFramesInHtml },
        MarkAsSpamEmbedTagsInHtml: { type: 'string', const: formValue.MarkAsSpamEmbedTagsInHtml },
        MarkAsSpamFormTagsInHtml: { type: 'string', const: formValue.MarkAsSpamFormTagsInHtml },
        MarkAsSpamWebBugsInHtml: { type: 'string', const: formValue.MarkAsSpamWebBugsInHtml },
        MarkAsSpamSensitiveWordList: { type: 'string', const: formValue.MarkAsSpamSensitiveWordList },
        MarkAsSpamSpfRecordHardFail: { type: 'string', const: formValue.MarkAsSpamSpfRecordHardFail },
        MarkAsSpamFromAddressAuthFail: { type: 'string', const: formValue.MarkAsSpamFromAddressAuthFail },
        MarkAsSpamNdrBackscatter: { type: 'string', const: formValue.MarkAsSpamNdrBackscatter },

        TestModeAction: { type: 'string', const: formValue.TestModeAction },

        PhishZapEnabled: { type: 'boolean', const: formValue.PhishZapEnabled ?? false },
        SpamZapEnabled: { type: 'boolean', const: formValue.SpamZapEnabled ?? false },

        EnableLanguageBlockList: { type: 'boolean', const: formValue.EnableLanguageBlockList },
        EnableRegionBlockList: { type: 'boolean', const: formValue.EnableRegionBlockList },

        AllowedSenders: buildArrayProp(formValue.AllowedSenders),
        AllowedSenderDomains: buildArrayProp(formValue.AllowedSenderDomains),
        BlockedSenders: buildArrayProp(formValue.BlockedSenders),
        BlockedSenderDomains: buildArrayProp(formValue.BlockedSenderDomains),
    };

    // Quarantine tags: only present when the corresponding action is Quarantine
    if (formValue.SpamAction === 'Quarantine') {
        properties.SpamQuarantineTag = { type: 'string', const: formValue.SpamQuarantineTag };
    }
    if (formValue.HighConfidenceSpamAction === 'Quarantine') {
        properties.HighConfidenceSpamQuarantineTag = {
            type: 'string',
            const: formValue.HighConfidenceSpamQuarantineTag,
        };
    }
    if (formValue.PhishSpamAction === 'Quarantine') {
        properties.PhishQuarantineTag = { type: 'string', const: formValue.PhishQuarantineTag };
    }
    if (formValue.HighConfidencePhishAction === 'Quarantine') {
        properties.HighConfidencePhishQuarantineTag = {
            type: 'string',
            const: formValue.HighConfidencePhishQuarantineTag,
        };
    }
    if (formValue.BulkSpamAction === 'Quarantine') {
        properties.BulkQuarantineTag = { type: 'string', const: formValue.BulkQuarantineTag };
    }

    // BCC recipients: only present when TestModeAction is BccMessage and value is set
    if (formValue.TestModeAction === 'BccMessage' && formValue.TestModeBccToRecipients) {
        properties.TestModeBccToRecipients = buildArrayProp([formValue.TestModeBccToRecipients]);
    }

    // Conditional: only present when the corresponding action is selected
    if (actionKeys.some((k) => formValue[k] === 'AddXHeader')) {
        properties.AddXHeaderValue = { type: 'string', const: formValue.AddXHeaderValue ?? '' };
    }

    if (actionKeys.some((k) => formValue[k] === 'ModifySubject')) {
        properties.ModifySubjectValue = { type: 'string', const: formValue.ModifySubjectValue ?? '' };
    }

    if ([...actionKeys, 'HighConfidencePhishAction'].some((k) => formValue[k] === 'Redirect')) {
        properties.RedirectToRecipients = {
            type: 'array',
            items: { type: 'string', const: formValue.RedirectToRecipients ?? '' },
            minItems: formValue.RedirectToRecipients ? 1 : 0,
            maxItems: formValue.RedirectToRecipients ? 1 : 0,
        };
    }

    // Language / region block lists: only present when enabled and non-empty
    if (formValue.EnableLanguageBlockList) {
        properties.LanguageBlockList = buildArrayProp(formValue.LanguageBlockList);
    }

    if (formValue.EnableRegionBlockList) {
        properties.RegionBlockList = buildArrayProp(formValue.RegionBlockList);
    }

    return {
        type: 'array',
        contains: {
            type: 'object',
            properties,
        },
    };
}
